import { useState } from 'react';
import Head from 'next/head';

// Implementations based on
// https://github.com/paulirish/lite-youtube-embed

// YT player params
// https://developers.google.com/youtube/player_parameters#Parameters

type Props = {
  videoid: string;
  playlabel?: string;
  autoplay?: boolean;
  playsinline?: boolean;
  controls?: boolean;
  mute?: boolean;
  width?: number | string;
  height?: number | string;
};

const YoutubePlayer = ({
  videoid,
  playlabel = 'Play',
  mute = false,
  controls = true,
  playsinline = false,
  autoplay = false,
  width,
  height,
}: Props) => {
  const [preconnected, setPreconnected] = useState(false);
  const encodedVideoId = encodeURIComponent(videoid);

  const warmConnection = () => {
    if (preconnected) return;
    setPreconnected(true);
  };

  const getParams = () => {
    return new URLSearchParams({
      ...(mute ? { mute: '1' } : { mute: '0' }),
      ...(controls ? { controls: '1' } : { controls: '0' }),
      ...(playsinline ? { playsinline: '1' } : { playsinline: '0' }),
      ...(autoplay ? { autoplay: '1' } : { autoplay: '0' }),
    });
  };

  return (
    <>
      {preconnected && (
        <Head>
          <link rel="preconnect" href="https://www.youtube-nocookie.com" key="pre-yt" />
          <link rel="preconnect" href="https://www.google.com" key="pre-google" />
          <link rel="preconnect" href="https://static.doubleclick.net" key="pre-dc" />
          <link rel="preconnect" href="https://googleads.g.doubleclick.net" key="pre-google-dc" />
        </Head>
      )}
      <article
        className="w-full h-full flex justify-center items-center"
        onPointerOver={warmConnection}
      >
        <iframe
          id="youtube-player"
          className="border-0"
          width={width || 560}
          height={height || 315}
          src={`https://www.youtube-nocookie.com/embed/${encodedVideoId}?${getParams().toString()}`}
          title={playlabel}
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen={true}
        ></iframe>
      </article>
    </>
  );
};

export default YoutubePlayer;
